import { useSphereDialog } from "@components/common/dialog/sphere-dialog-provider";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { Button, blue, neutral, Text, useToast } from "@flat-ui";
import { Box, Stack, Typography } from "@mui/material";
import { appStateSelector } from "@store/app/app-selector";
import { selectedProjectSelector } from "@store/projects/projects-selector";
import { updateProjectDetails } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  isProjectPreparableForDownload,
  isProjectReadyForVaultDownload,
  openInSphereVault,
} from "@utils/project-utils";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";

/** Which prepare action is currently in-flight, if any. */
type PendingAction = "prepare" | "prepareAndDownload" | null;

interface PrepareDownloadActionsProps {
  /** Which prepare action is currently in-flight, if any. */
  pendingAction: PendingAction;

  /** Prepare the project and immediately open it in Sphere Vault. */
  onPrepareAndDownload: () => void;

  /** Prepare the project without opening Sphere Vault. */
  onPrepare: () => void;
}

/** Buttons shown while the project can still be prepared for download. */
function PrepareDownloadActions({
  pendingAction,
  onPrepareAndDownload,
  onPrepare,
}: PrepareDownloadActionsProps): JSX.Element {
  return (
    <Stack direction="row" spacing={2}>
      <Button
        onClick={onPrepareAndDownload}
        isLoading={pendingAction === "prepareAndDownload"}
        disabled={pendingAction !== null}
      >
        Prepare and download
      </Button>
      <Button
        variant="secondary"
        onClick={onPrepare}
        isLoading={pendingAction === "prepare"}
        disabled={pendingAction !== null}
      >
        Prepare for download
      </Button>
    </Stack>
  );
}

/**
 * Settings section for the Sphere Vault offline-download flow. It has two states:
 *
 * - **Preparable** (project is plainly `ARCHIVED`): offers "Prepare for download"
 *   / "Prepare and download", which transition the project to
 *   `ARCHIVED_READY_TO_DOWNLOAD`.
 * - **Ready** (`ARCHIVED_READY_TO_DOWNLOAD` or `ARCHIVED_DOWNLOADED`): confirms the
 *   project is ready and offers a "Download" button so the user isn't left
 *   wondering where the export went after preparing. This mirrors the Download
 *   action in the project overview's top-right actions.
 *
 * This is a beta feature kept intentionally low-profile in the project Settings
 * tab. It renders nothing unless the beta flag is on and the project is either
 * preparable or already ready to download.
 */
export function PrepareProjectDownload(): JSX.Element | null {
  const { isBetaTestingEnabled } = useAppSelector(appStateSelector);
  const selectedProject = useAppSelector(selectedProjectSelector);
  const { createDialog } = useSphereDialog();
  const { openToast } = useToast();
  const { handleErrorWithToast } = useErrorContext();
  const { trackEvent } = useTrackEvent();
  const dispatch = useAppDispatch();
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);

  if (
    !isBetaTestingEnabled ||
    !selectedProject ||
    !(
      isProjectPreparableForDownload(selectedProject.archivingState) ||
      isProjectReadyForVaultDownload(selectedProject.archivingState)
    )
  ) {
    return null;
  }

  const isReadyToDownload = isProjectReadyForVaultDownload(
    selectedProject.archivingState
  );

  /** Confirmation dialog shown before any prepare action. */
  async function confirmPrepare(): Promise<boolean> {
    return createDialog(
      {
        title: "Prepare project for download",
        confirmText: "Prepare",
        isHideClose: true,
      },
      <Typography>
        This prepares an offline copy of the project so you can download it in
        Sphere Vault. Only one project can be ready to download at a time, so
        any other project you previously prepared will move back to archived.
      </Typography>
    );
  }

  /**
   * Transitions the project to ready-to-download. Returns true on success and
   * surfaces a toast on failure. `action` controls which button shows a spinner.
   */
  async function runPrepare(
    action: "prepare" | "prepareAndDownload"
  ): Promise<boolean> {
    // `selectedProject` is guaranteed defined here (guarded above), but narrow
    // again for the closure.
    if (!selectedProject) {
      return false;
    }

    trackEvent({ name: ProjectEvents.prepareProjectForDownload });
    setPendingAction(action);

    try {
      await dispatch(
        updateProjectDetails({
          projectId: selectedProject.id,
          payload: { archivingState: "ARCHIVED_READY_TO_DOWNLOAD" },
        })
      ).unwrap();
      return true;
    } catch (error) {
      handleErrorWithToast({
        id: `prepare-project-download-${selectedProject.id}`,
        title: "Couldn't prepare this project for download",
        error,
      });
      return false;
    } finally {
      setPendingAction(null);
    }
  }

  /** Prepare the project; the user downloads later from the project overview. */
  async function prepareForDownload(): Promise<void> {
    if (!(await confirmPrepare())) {
      return;
    }
    if (await runPrepare("prepare")) {
      openToast({
        title: "Your project is ready to download in Sphere Vault",
      });
    }
  }

  /** Prepare the project and immediately open it in Sphere Vault to download. */
  async function prepareAndDownload(): Promise<void> {
    if (!selectedProject || !(await confirmPrepare())) {
      return;
    }
    if (await runPrepare("prepareAndDownload")) {
      openInSphereVault({ projectId: selectedProject.id, trackEvent });
    }
  }

  /** Open the already-prepared project in Sphere Vault to download it. */
  function downloadFromVault(): void {
    if (!selectedProject) {
      return;
    }
    openInSphereVault({ projectId: selectedProject.id, trackEvent });
  }

  return (
    <Box
      component="div"
      sx={{
        mt: "40px",
        backgroundColor: neutral[50],
        borderRadius: "8px",
        padding: "50px",
      }}
    >
      <Text variant="heading24" component="div" color={blue[400]}>
        Download project
      </Text>
      <Text
        variant="bodyL"
        component="div"
        color={neutral[600]}
        sx={{ mt: "8px", mb: "32px" }}
      >
        {isReadyToDownload
          ? "This project is ready to download in Sphere Vault. You can also use the Download action in the top-right of this page."
          : "Prepare an offline copy of this archived project, then download it in Sphere Vault. Only one project can be ready to download at a time."}
      </Text>
      {isReadyToDownload ? (
        <Button onClick={downloadFromVault}>Download</Button>
      ) : (
        <PrepareDownloadActions
          pendingAction={pendingAction}
          onPrepareAndDownload={prepareAndDownload}
          onPrepare={prepareForDownload}
        />
      )}
    </Box>
  );
}
